import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useContext, useMemo, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { CARD_UPDATE_MODAL_ANCHOR_NAME } from '@shared/scenes/cards/form/update/cardUpdateModalAnchorName';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { useViewTemplate } from '@shared/scenes/workflows/hooks';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { SUBTABLES, useFeatureFlag } from '@shared/util/featureFlags';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { AddMoreButton } from '../AddMoreButton';
import { EditFieldContainer } from '../EditFieldContainer';
import { SubtableList } from './SubtableList';
import { SubtableRowMenu } from './SubtableRowMenu';
import { SubtableTable } from './SubtableTable';
import { SubtableTableModal } from './SubtableTableModal';
import { getSubtableColumns } from './subtableColumns';
import { useSubtableRowMenu } from './useSubtableRowMenu';
import { useSubtableRows } from './useSubtableRows';
import styles from './SubtableEditField.module.css';

interface Props {
  component: API.SubtableComponent;
  entityId: string;
  style?: CSSProperties;
}

/**
 * The rows this card owns in a subtable. Not rendered on the Create form: rows need a saved
 * parent card to belong to.
 */
export function SubtableEditField(props: Props) {
  const isSubtablesEnabled = useFeatureFlag(SUBTABLES);
  const formType = useContext(CardFormTypeContext);

  if (!isSubtablesEnabled || formType === 'CREATE') {
    return null;
  }
  return <SubtableEditFieldContent {...props} />;
}

function SubtableEditFieldContent({ component, entityId, style }: Props) {
  const { rows, refs, totalResults, hasMore, loadMore, refetch, loading, error, workflowTemplate } =
    useSubtableRows(component, entityId);
  const isDesktop = useIsDesktop();
  const [showSubtableModal, setShowSubtableModal] = useState(false);
  const rowMenu = useSubtableRowMenu();
  // The expanded modal's own row menu; see SubtableTableModal's rowMenu prop.
  const modalRowMenu = useSubtableRowMenu();
  // Close the row menu if the user opens the subtable modal.
  const openSubtableModal = () => {
    rowMenu.close();
    setShowSubtableModal(true);
  };
  // Mobile adds rows in the expanded view: open it with its new-row sheet already up, so the new
  // row lands in the full list rather than the card form's short preview.
  const addRow = () => {
    if (isDesktop) {
      rowMenu.openNew();
    } else {
      openSubtableModal();
      modalRowMenu.openNew();
    }
  };
  // Columns track the Update view template, when someone has customized one: wait for it, so
  // columns it hides don't flash in first.
  const updateViewTemplateId = workflowTemplate?.viewTemplateDefaults?.update;
  const { viewTemplate: updateViewTemplate, loading: updateViewTemplateLoading } = useViewTemplate(
    workflowTemplate?.id,
    updateViewTemplateId,
  );
  const isColumnsLoading = !isNil(updateViewTemplateId) && updateViewTemplateLoading;
  const columns = useMemo(() => {
    return getSubtableColumns(workflowTemplate, updateViewTemplate);
  }, [workflowTemplate, updateViewTemplate]);
  const isInitialLoad = (loading && isEmpty(rows)) || isColumnsLoading;
  const hasError = !isNil(error);
  const isEmptyState = !loading && !hasError && isEmpty(rows);
  // Read-only from the field's own setting or a view template override, or because the whole card
  // is read-only (e.g. a public external form), which stamps readonly onto every component.
  const isReadOnly = !!component.readonly;
  const emptyStatePlaceholder = isReadOnly
    ? i18n('card.blocks.subtableInput.noRows')
    : i18n('card.blocks.subtableInput.addFirstRow');
  const errorPlaceholder = i18n('card.blocks.subtableInput.loadError');
  const placeholder = hasError ? errorPlaceholder : emptyStatePlaceholder;
  const showPlaceholder = isEmptyState || hasError;
  const label = getLabelWithCount(component, hasError ? 0 : totalResults);
  const tableProps = {
    workflowTemplateId: workflowTemplate?.id ?? '',
    columns,
    rows,
    refs,
    hasMore,
    onLoadMore: loadMore,
  };

  return (
    <>
      <EditFieldContainer
        component={component}
        label={label}
        showIcon={showPlaceholder}
        showPlaceholder={showPlaceholder}
        placeholder={placeholder}
        largerInput={false}
        style={style}
        isReadOnly={isReadOnly}
        onPress={isEmptyState && !isReadOnly ? rowMenu.openNew : undefined}
        footer={
          !isEmpty(rows) &&
          !isReadOnly && (
            <View className={styles.footer}>
              <SubtableAddRowButton component={component} onPress={addRow} />
            </View>
          )
        }
      >
        {isInitialLoad && <LoadingIndicator />}
        {!isEmpty(rows) && !isColumnsLoading && (
          <View className={styles.tableFrame}>
            {isDesktop && (
              <SubtableTable
                {...tableProps}
                selectedRowId={rowMenu.selectedRowId}
                onRowPress={rowMenu.openRow}
              />
            )}
            {!isDesktop && (
              <SubtableList
                columns={columns}
                rows={rows}
                refs={refs}
                totalCount={totalResults}
                onShowMore={openSubtableModal}
                selectedRowId={rowMenu.selectedRowId}
                onRowPress={rowMenu.openRow}
              />
            )}
            {/* Mobile opens the expanded view from "Show N more" and Add row instead. */}
            {isDesktop && (
              <Button
                variant="ghost"
                size="medium"
                icon="arrows-expand"
                iconOnly
                label={i18n('common.expand')}
                tooltip={i18n('common.expand')}
                className={styles.expandButton}
                onClick={openSubtableModal}
              />
            )}
          </View>
        )}
      </EditFieldContainer>
      {/* Render modals and overlays next to the field, not inside it, to avoid inheriting focus-visible styles. */}
      {!isEmpty(rows) && (
        <SubtableTableModal
          {...tableProps}
          component={component}
          workflowTemplate={workflowTemplate}
          parentEntityId={entityId}
          onRowSaved={refetch}
          title={label ?? getComponentLabel(component) ?? ''}
          isReadOnly={isReadOnly}
          isOpen={showSubtableModal}
          onOpenChange={setShowSubtableModal}
          rowMenu={modalRowMenu}
        />
      )}
      <SubtableRowMenu
        workflowTemplate={workflowTemplate}
        parentEntityId={entityId}
        isOpen={rowMenu.isOpen}
        row={rowMenu.row}
        isReadOnly={isReadOnly}
        containerAnchorName={CARD_UPDATE_MODAL_ANCHOR_NAME}
        onSaved={refetch}
        onClose={rowMenu.close}
      />
    </>
  );
}

/** "+ Add row" under the field's rows; opens the add-row menu. */
function SubtableAddRowButton({
  component,
  onPress,
}: {
  component: API.SubtableComponent;
  onPress: () => void;
}) {
  return (
    <AddMoreButton
      component={component}
      label={i18n('card.blocks.subtableInput.addRow')}
      onPress={onPress}
    />
  );
}

/** "Line Items (3)" once there are rows, like Related Card fields; the plain label otherwise. */
function getLabelWithCount(component: API.SubtableComponent, count: number): string | undefined {
  if (count > 0) {
    return `${getComponentLabel(component)} (${count})`.trim();
  }
  return undefined;
}
